'use client';

// PORTAL CIUDADANO (página pública, SIN sidebar). Cabecera de marca institucional
// (verde, mismo lenguaje que el Login) y dos pestañas:
//  - "Radicar notificación disciplinaria": formulario con checkbox anónimo (oculta datos del quejoso);
//    al enviar muestra el radicado generado en un recuadro destacado con botón
//    "copiar" y aviso de guardarlo.
//  - "Consultar estado": radicado + correo → estado general, etapa y última
//    actuación pública; y sección para aportar un documento (fetch público).
// Incluye un enlace "Ingreso funcionarios/partes" para volver al Login.

import { useRef, useState } from 'react';
import {
  Check,
  Copy,
  FileText,
  FileUp,
  Loader2,
  LogIn,
  Scale,
  Search,
  Send,
} from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import {
  aportarDocumentoPublico,
  consultarEstado,
  radicarQueja,
  type ConsultaEstadoResp,
  type RadicarQuejaResp,
} from '@/lib/portal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

function fmtFecha(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}

export default function PortalPublico({ onIngresar }: { onIngresar: () => void }) {
  return (
    <div className="min-h-screen bg-background">
      {/* Cabecera de marca (verde institucional) */}
      <header className="bg-sidebar text-sidebar-foreground">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-6 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-sidebar-primary text-sidebar-primary-foreground">
              <Scale className="size-6" />
            </span>
            <div>
              <p className="text-lg font-semibold tracking-tight">Módulo Disciplinario</p>
              <p className="text-sm text-sidebar-foreground/70">
                Portal Ciudadano
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            className="border-sidebar-border bg-transparent text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            onClick={onIngresar}
          >
            <LogIn className="size-4" /> Ingreso funcionarios/partes
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        <div className="mb-6 max-w-2xl space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Atención a la ciudadanía
          </h1>
          <p className="text-muted-foreground">
            Radique una notificación disciplinaria o consulte el estado de un proceso con
            su radicado y correo. No requiere iniciar sesión.
          </p>
        </div>

        <Tabs defaultValue="radicar" className="w-full">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="radicar">
              <Send className="size-4" /> Radicar notificación disciplinaria
            </TabsTrigger>
            <TabsTrigger value="consultar">
              <Search className="size-4" /> Consultar estado
            </TabsTrigger>
          </TabsList>

          <TabsContent value="radicar" className="mt-4">
            <FormRadicar />
          </TabsContent>
          <TabsContent value="consultar" className="mt-4">
            <FormConsultar />
          </TabsContent>
        </Tabs>

        <p className="mt-10 text-center text-xs text-muted-foreground">
          Módulo Disciplinario
        </p>
      </main>
    </div>
  );
}

// ===========================================================================
// Pestaña: Radicar notificación disciplinaria
// ===========================================================================

function FormRadicar() {
  const [anonimo, setAnonimo] = useState(false);
  const [descripcionHechos, setDescripcionHechos] = useState('');
  const [fechaHechos, setFechaHechos] = useState('');
  const [lugar, setLugar] = useState('');
  const [investigadoNombre, setInvestigadoNombre] = useState('');
  const [entidadNombre, setEntidadNombre] = useState('');
  const [nombre, setNombre] = useState('');
  const [correo, setCorreo] = useState('');
  const [telefono, setTelefono] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<RadicarQuejaResp | null>(null);
  const [copiado, setCopiado] = useState(false);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!descripcionHechos.trim()) {
      avisoError('Describa los hechos de la notificación disciplinaria.');
      return;
    }
    if (!anonimo && !correo.trim()) {
      avisoError('El correo del quejoso es obligatorio si la queja no es anónima.');
      return;
    }
    setEnviando(true);
    try {
      const resp = await radicarQueja({
        descripcionHechos: descripcionHechos.trim(),
        ...(fechaHechos ? { fechaHechos } : {}),
        ...(lugar.trim() ? { lugar: lugar.trim() } : {}),
        ...(investigadoNombre.trim() ? { investigadoNombre: investigadoNombre.trim() } : {}),
        ...(entidadNombre.trim() ? { entidadNombre: entidadNombre.trim() } : {}),
        anonimo,
        ...(anonimo
          ? {}
          : {
              quejoso: {
                correo: correo.trim(),
                ...(nombre.trim() ? { nombre: nombre.trim() } : {}),
                ...(telefono.trim() ? { telefono: telefono.trim() } : {}),
              },
            }),
      });
      setResultado(resp);
      avisoExito('Notificación disciplinaria radicada.', resp.mensaje);
    } catch (err) {
      avisoError('No se pudo radicar la notificación disciplinaria', err instanceof Error ? err.message : undefined);
    } finally {
      setEnviando(false);
    }
  }

  async function copiar() {
    if (!resultado) return;
    try {
      await navigator.clipboard.writeText(resultado.radicado);
      setCopiado(true);
      avisoExito('Radicado copiado.');
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      avisoError('No se pudo copiar el radicado.');
    }
  }

  function nueva() {
    setResultado(null);
    setAnonimo(false);
    setDescripcionHechos('');
    setFechaHechos('');
    setLugar('');
    setInvestigadoNombre('');
    setEntidadNombre('');
    setNombre('');
    setCorreo('');
    setTelefono('');
  }

  // Estado: queja radicada → recuadro destacado con el radicado.
  if (resultado) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Check className="size-5 text-primary" /> Notificación disciplinaria radicada
          </CardTitle>
          <CardDescription>{resultado.mensaje}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-5 text-center">
            <p className="text-sm text-muted-foreground">Su número de radicado</p>
            <div className="mt-1 flex items-center justify-center gap-2">
              <p className="text-2xl font-bold tracking-tight text-primary">
                {resultado.radicado}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={copiar}
                aria-label="Copiar radicado"
              >
                {copiado ? <Check className="size-4" /> : <Copy className="size-4" />}
                {copiado ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
            <p className="mt-3 text-sm font-medium text-foreground">
              Guarde este radicado: lo necesitará (junto con su correo) para
              consultar el estado del proceso.
            </p>
            <Badge variant="secondary" className="mt-2">
              Estado: {resultado.estadoActual}
            </Badge>
          </div>
          <Button type="button" variant="outline" onClick={nueva}>
            Radicar otra notificación disciplinaria
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="size-5 text-primary" /> Radicar notificación disciplinaria
        </CardTitle>
        <CardDescription>
          Diligencie los hechos. Los campos marcados con * son obligatorios.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={enviar} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción de los hechos *</Label>
            <Textarea
              id="descripcion"
              rows={5}
              placeholder="Narre de forma clara y detallada los hechos que motivan la notificación disciplinaria."
              value={descripcionHechos}
              onChange={(e) => setDescripcionHechos(e.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="fechaHechos">Fecha de los hechos</Label>
              <Input
                id="fechaHechos"
                type="date"
                value={fechaHechos}
                onChange={(e) => setFechaHechos(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lugar">Lugar de los hechos</Label>
              <Input
                id="lugar"
                placeholder="Ciudad, dependencia, dirección…"
                value={lugar}
                onChange={(e) => setLugar(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="investigado">Persona investigada</Label>
              <Input
                id="investigado"
                placeholder="Nombre del presunto responsable"
                value={investigadoNombre}
                onChange={(e) => setInvestigadoNombre(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="entidad">Entidad</Label>
              <Input
                id="entidad"
                placeholder="Entidad o dependencia"
                value={entidadNombre}
                onChange={(e) => setEntidadNombre(e.target.value)}
              />
            </div>
          </div>

          <Separator />

          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={anonimo}
              onChange={(e) => setAnonimo(e.target.checked)}
              className="size-4 accent-primary"
            />
            Radicar de forma anónima (no se solicitan sus datos)
          </label>

          {!anonimo && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre del quejoso</Label>
                  <Input
                    id="nombre"
                    placeholder="Su nombre completo"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="correo">Correo electrónico *</Label>
                  <Input
                    id="correo"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={correo}
                    onChange={(e) => setCorreo(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2 sm:max-w-xs">
                <Label htmlFor="telefono">Teléfono</Label>
                <Input
                  id="telefono"
                  inputMode="tel"
                  placeholder="Opcional"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Su correo es necesario para consultar el estado del proceso más
                adelante.
              </p>
            </div>
          )}

          <Button type="submit" disabled={enviando} className="w-full sm:w-auto">
            {enviando ? (
              <>
                <Loader2 className="size-4 animate-spin" /> Enviando…
              </>
            ) : (
              <>
                <Send className="size-4" /> Radicar notificación disciplinaria
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// Pestaña: Consultar estado
// ===========================================================================

function FormConsultar() {
  const [radicado, setRadicado] = useState('');
  const [correo, setCorreo] = useState('');
  const [consultando, setConsultando] = useState(false);
  const [estado, setEstado] = useState<ConsultaEstadoResp | null>(null);

  async function consultar(e: React.FormEvent) {
    e.preventDefault();
    if (!radicado.trim() || !correo.trim()) {
      avisoError('Ingrese el radicado y el correo con que radicó.');
      return;
    }
    setConsultando(true);
    setEstado(null);
    try {
      const resp = await consultarEstado(radicado.trim(), correo.trim());
      setEstado(resp);
    } catch (err) {
      avisoError('No se pudo consultar el estado', err instanceof Error ? err.message : undefined);
    } finally {
      setConsultando(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="size-5 text-primary" /> Consultar estado
          </CardTitle>
          <CardDescription>
            Ingrese el radicado y el correo con que radicó la notificación disciplinaria.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={consultar} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="c-radicado">Radicado</Label>
                <Input
                  id="c-radicado"
                  placeholder="Ej: DISC-2026-00001"
                  value={radicado}
                  onChange={(e) => setRadicado(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-correo">Correo electrónico</Label>
                <Input
                  id="c-correo"
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={correo}
                  onChange={(e) => setCorreo(e.target.value)}
                />
              </div>
            </div>
            <Button type="submit" disabled={consultando}>
              {consultando ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Consultando…
                </>
              ) : (
                <>
                  <Search className="size-4" /> Consultar
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {estado && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="size-5 text-primary" /> Radicado {estado.radicado}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Estado general</p>
                  <Badge variant="secondary" className="mt-1">
                    {estado.estadoGeneral}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Etapa</p>
                  <Badge variant="secondary" className="mt-1">
                    {estado.etapa}
                  </Badge>
                </div>
              </div>
              <Separator />
              <div>
                <p className="text-sm text-muted-foreground">Última actuación pública</p>
                {estado.ultimaActuacionPublica ? (
                  <p className="mt-1 text-sm font-medium">
                    {estado.ultimaActuacionPublica.tipo}
                    {' · '}
                    {fmtFecha(estado.ultimaActuacionPublica.fecha)}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Sin actuaciones públicas registradas.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <AportarDocumento radicado={estado.radicado} correo={correo.trim()} />
        </>
      )}
    </div>
  );
}

// ---- Sección: aportar documento (público, multipart sin token) ------------

function AportarDocumento({ radicado, correo }: { radicado: string; correo: string }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);

  async function subirArchivo() {
    if (!file) {
      avisoError('Seleccione un archivo primero.');
      return;
    }
    setSubiendo(true);
    try {
      await aportarDocumentoPublico(radicado, file, correo);
      avisoExito('Documento aportado.');
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      avisoError('No se pudo aportar el documento', err instanceof Error ? err.message : undefined);
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileUp className="size-5 text-primary" /> Aportar documento
        </CardTitle>
        <CardDescription>
          Adjunte un documento de soporte a este radicado.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/*,.pdf,.doc,.docx"
            disabled={subiendo}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="cursor-pointer"
          />
          <Button
            type="button"
            onClick={subirArchivo}
            disabled={subiendo || !file}
            className="shrink-0"
          >
            {subiendo ? (
              <>
                <Loader2 className="size-4 animate-spin" /> Subiendo…
              </>
            ) : (
              <>
                <FileUp className="size-4" /> Aportar
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
